import React, { useCallback, useMemo, useState } from "react";
import { Text, Paper, Group, Box, ScrollArea, Tooltip } from "@mantine/core";
import moment from "moment-timezone";
import { GetTransactionsResponse } from "../../../../../types/transactions";
import { Employee } from "../../../../../types/salon";
import { useCalendar } from "../../../../../context/CalendarContext";
import { useSalon } from "../../../../../context/SalonContext";
import {
  CARD_BOARD_COLUMNS,
  CARD_VIEW_COLUMNS,
  getAppointmentStatusColor,
} from "../../../../../types/calendar";
import { EventFlags } from "./EventBlock";
import { transactionFlags } from "../../flags/calendarFlags";
import { badgeSettingsFrom } from "../../../../../utils/badgeSettings";
import blockStyles from "./EventBlock.module.css";
import {
  isUnconfirmedStatus,
  statusPickBlockedReason,
} from "../../booking/guards";
import { notifyWarning } from "../../../../../utils/notify";
import StaffAvatar from "../../../../common/StaffAvatar/StaffAvatar";
import CalendarToolbar from "./CalendarToolbar";
import styles from "./CardView.module.css";

/**
 * A column's count pill and drag-target highlight are tinted from the ONE
 * status→color source of truth (`getAppointmentStatusColor` in
 * `types/calendar.ts`, shared with the Filters dropdown Status column), so a status
 * reads the same color on its card as it does in that filter. We take the
 * Mantine color *name* it returns and pull the soft fill + bold accent straight
 * from the Mantine ramp CSS vars — shade 0 for the tint, shade 7 for the
 * accent — rather than hardcoding hexes or maintaining a parallel palette.
 */
/** Status pill tokens per board column (see --status-* in index.css). */
const COLUMN_STATUS_TOKEN: Record<string, string> = {
  Requested: "requested",
  unconfirmed: "accepted",
  Confirmed: "confirmed",
  Completed: "completed",
};

/** Column accent + tint: the status pill tokens, else the Mantine ramp
 *  (Arrived keeps the brand lime). */
const columnAccentStyle = (column: {
  id: string;
  colorName: string;
}): React.CSSProperties => {
  const token = COLUMN_STATUS_TOKEN[column.id];
  return (
    token
      ? {
          "--column-accent": `var(--status-${token}-ink)`,
          "--column-tint": `var(--status-${token}-bg)`,
        }
      : {
          "--column-accent": `var(--mantine-color-${column.colorName}-7)`,
          "--column-tint": `var(--mantine-color-${column.colorName}-0)`,
        }
  ) as React.CSSProperties;
};

/*
 * Note on typography: font size/weight for the column header and card text are
 * set with Mantine's `fz`/`fw` props rather than in CardView.module.css. Both
 * the module class and Mantine's `.mantine-Text-root` are single-class
 * selectors, so a font-size in the module would only win on injection order —
 * the props write inline custom properties and always win. Color, truncation
 * and layout stay in the CSS module.
 */

/**
 * ─── UNCONFIRMED COLUMN ─────────────────────────────────────────────────────
 *
 * The board has no Accepted column: Accepted appointments are awaiting the
 * client's confirmation (salons that need confirmation put online bookings in
 * Accepted; the reminder texts ask the client to confirm), so their column is
 * titled "Accepted (Unconfirmed)" (`isUnconfirmedStatus`). It is the Accepted column in
 * every other respect: each card appears once, a drop writes "Accepted"
 * (same optimistic flow as the other columns), and the Filters "Accepted"
 * selection shows/hides it. Its board position is fixed by COLUMN_ORDER below.
 */
const UNCONFIRMED_COLUMN_ID = "unconfirmed";
// The Unconfirmed column's count-pill color, named the same way
// `STATUS_COLORS` names real statuses (a Mantine color, not a hex).
const UNCONFIRMED_COLUMN_COLOR = "gray";

/** The board column a status's cards render in. */
const columnIdFor = (status: string): string =>
  isUnconfirmedStatus(status) ? UNCONFIRMED_COLUMN_ID : status;

/**
 * The fixed left-to-right order of every column on the board (by column id),
 * following the appointment lifecycle. Columns always
 * render in this order regardless of the order the user enabled them in the
 * Filters dropdown; anything not listed falls to the end.
 */
const COLUMN_ORDER: string[] = [
  "Requested",
  UNCONFIRMED_COLUMN_ID,
  "Confirmed",
  "Arrived",
  "Completed",
  "No Show",
  "Declined",
  "Cancelled",
];

const orderIndex = (id: string): number => {
  const i = COLUMN_ORDER.indexOf(id);
  return i === -1 ? COLUMN_ORDER.length : i;
};

interface CardViewProps {
  transactions: GetTransactionsResponse[];
  onSelectTransaction: (transaction: GetTransactionsResponse) => void;
  /**
   * Persist a status change made by dragging a card into another column. When
   * omitted the board is read-only (cards stay clickable but not draggable).
   */
  onStatusChange?: (
    transaction: GetTransactionsResponse,
    newStatus: string,
  ) => Promise<void>;
}

interface ColumnDescriptor {
  /** Column id: the status, or "unconfirmed" for the Accepted column. */
  id: string;
  /** The status this column holds and writes on drop. */
  status: string;
  title: string;
  /** Mantine color name (from `getAppointmentStatusColor`) used to tint the
   *  count pill and drag highlight — see `columnAccentStyle`. */
  colorName: string;
  /** Cards may be dropped here (false when the board is read-only). */
  isDroppable: boolean;
  /** Completing an appointment means taking payment, so a drop here opens the
   *  transaction modal instead of writing the status directly. */
  opensTransactionOnDrop: boolean;
}

/** In-flight drag: which card, and its status when the drag started (the
 *  optimistic one while a previous drop is saving). */
interface DragState {
  transaction: GetTransactionsResponse;
  fromStatus: string | null;
}

/**
 * "9:00 AM – 11:30 AM" reads better as "9:00 – 11:30 AM" when both ends share a
 * meridiem, which is the common case. Matches the design reference.
 */
const formatTimeRange = (start: string, end: string): string => {
  const meridiem = /\s(AM|PM)$/.exec(start);
  if (meridiem && end.endsWith(meridiem[1])) {
    return `${start.replace(/\s(AM|PM)$/, "")} – ${end}`;
  }
  return `${start} – ${end}`;
};

const CardView: React.FC<CardViewProps> = ({
  transactions,
  onSelectTransaction,
  onStatusChange,
}) => {
  const {
    selectedStatuses,
    selectedServiceTypes,
    selectedArtists,
    selectedDate,
    setSelectedDate,
    filteredEmployees,
  } = useCalendar();
  const { businessInfo } = useSalon() as {
    businessInfo: { timezone: string } | null;
  };

  // The parent passes a fresh onStatusChange each render, so column building
  // keys off whether dragging is enabled rather than the callback identity.
  const canDrag = !!onStatusChange;

  // The board shows a single day — whichever the date picker has selected —
  // resolved in the business timezone so the day boundaries match the salon's,
  // not the viewer's.
  const dayRange = useMemo(() => {
    const timezone = businessInfo?.timezone || "America/New_York";
    return {
      start: moment(selectedDate).tz(timezone).startOf("day"),
      end: moment(selectedDate).tz(timezone).endOf("day"),
    };
  }, [selectedDate, businessInfo?.timezone]);

  // Create set of active staff IDs for efficient lookup
  const activeStaffIds = useMemo(() => {
    return new Set(filteredEmployees.map((emp) => emp.id));
  }, [filteredEmployees]);

  // Staff photo/name lookup so cards can show real profile pictures.
  const employeesById = useMemo(() => {
    const map = new Map<string, Employee>();
    filteredEmployees.forEach((emp) => map.set(emp.id, emp));
    return map;
  }, [filteredEmployees]);

  // Filter transactions by service type, staff, and the selected day
  const filteredTransactions = useMemo(() => {
    const timezone = businessInfo?.timezone || "America/New_York";
    return transactions.filter((t) => {
      // Filter out appointments from inactive staff members
      const hasActiveStaff = t.line_items.booked_services.some((s) =>
        activeStaffIds.has(s.staff_id),
      );
      if (!hasActiveStaff) return false;

      // Filter by service type
      if (!selectedServiceTypes.includes("all")) {
        const hasMatchingService = t.line_items.booked_services.some((s) =>
          selectedServiceTypes.includes(s.service_type),
        );
        if (!hasMatchingService) return false;
      }

      // Filter by staff
      if (!selectedArtists.includes("all")) {
        const hasMatchingStaff = t.line_items.booked_services.some((s) =>
          selectedArtists.includes(s.staff_id),
        );
        if (!hasMatchingStaff) return false;
      }

      // Filter to the selected day - convert service date to business timezone
      // before comparison
      const firstService = t.line_items.booked_services[0];
      if (!firstService || !firstService.start_dt) {
        // If there's no valid service date, exclude from the day filter
        return false;
      }

      const serviceDate = moment
        .utc(firstService.start_dt)
        .tz(timezone)
        .startOf("day");
      if (!serviceDate.isSame(dayRange.start, "day")) {
        return false;
      }

      return true;
    });
  }, [
    transactions,
    selectedServiceTypes,
    selectedArtists,
    activeStaffIds,
    dayRange,
    businessInfo?.timezone,
  ]);

  // Optimistic status per transaction id: applied to grouping the instant a
  // card is dropped, so it moves without waiting for the write to come back.
  // Cleared once the write settles — success leaves the real status in the
  // prop; failure reverts the card to where it came from.
  const [optimisticStatus, setOptimisticStatus] = useState<
    Record<string, string>
  >({});

  // Group transactions by status
  const groupedByStatus = useMemo(() => {
    const groups: Record<string, GetTransactionsResponse[]> = {};

    // Initialize all visible columns
    CARD_VIEW_COLUMNS.forEach((status) => {
      groups[columnIdFor(status)] = [];
    });

    filteredTransactions.forEach((transaction) => {
      const id = transaction.transaction.id;
      const status =
        optimisticStatus[id] ?? transaction.transaction.appointment_status;
      if (!status) return;
      const columnId = columnIdFor(status);
      if (groups[columnId] !== undefined) {
        groups[columnId].push(transaction);
      }
    });

    // Sort each group by date
    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => {
        const aDate = a.line_items.booked_services[0]?.start_dt;
        const bDate = b.line_items.booked_services[0]?.start_dt;
        return moment(aDate).diff(moment(bDate));
      });
    });

    return groups;
  }, [filteredTransactions, optimisticStatus]);

  // Build visible columns: the board's status columns that are still selected
  // in the Filters dropdown (Status column; "Accepted" controls Unconfirmed),
  // then order them by COLUMN_ORDER so the board layout is fixed regardless
  // of toggle order.
  const visibleColumns = useMemo(() => {
    const describe = (status: string): ColumnDescriptor => {
      const unconfirmed = isUnconfirmedStatus(status);
      return {
        id: columnIdFor(status),
        status,
        title: unconfirmed ? "Accepted (Unconfirmed)" : status,
        colorName: unconfirmed
          ? UNCONFIRMED_COLUMN_COLOR
          : getAppointmentStatusColor(status),
        isDroppable: canDrag,
        opensTransactionOnDrop: status === "Completed",
      };
    };

    const columns: ColumnDescriptor[] = selectedStatuses
      .filter((status) => CARD_BOARD_COLUMNS.includes(status))
      .map(describe);

    columns.sort((a, b) => orderIndex(a.id) - orderIndex(b.id));

    return columns;
  }, [selectedStatuses, canDrag]);

  // ── Drag and drop ────────────────────────────────────────────────────────
  // Native HTML5 drag events, which is what the design reference uses. Touch
  // devices don't fire them, so on mobile the board stays tap-to-open — a
  // card's status can still be changed from the transaction modal it opens.
  const [drag, setDrag] = useState<DragState | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  const handleDragStart = useCallback(
    (transaction: GetTransactionsResponse) => {
      const id = transaction.transaction.id;
      setDrag({
        transaction,
        fromStatus:
          optimisticStatus[id] ??
          transaction.transaction.appointment_status ??
          null,
      });
    },
    [optimisticStatus],
  );

  const handleDragEnd = useCallback(() => {
    setDrag(null);
    setOverColumnId(null);
  }, []);

  const handleDrop = useCallback(
    async (column: ColumnDescriptor) => {
      const dragged = drag;
      setDrag(null);
      setOverColumnId(null);

      if (!dragged || !column.isDroppable) return;
      // A drop onto the card's current status is a no-op, not a write.
      if (dragged.fromStatus === column.status) return;

      if (column.opensTransactionOnDrop) {
        onSelectTransaction(dragged.transaction);
        return;
      }

      if (!onStatusChange) return;

      // Same gate as the status menus (Arrived only within its window): no
      // write, the card stays where it was, and the reason is shown.
      const blockedReason = statusPickBlockedReason(
        column.status,
        dragged.transaction,
        businessInfo?.timezone || "America/New_York",
      );
      if (blockedReason) {
        notifyWarning(blockedReason, { title: "Can't mark as Arrived" });
        return;
      }

      const id = dragged.transaction.transaction.id;
      // Move the card into the target column right away, then write in the
      // background. On success the prop catches up to the same status; on
      // failure the toast fires and clearing the override snaps it back.
      setOptimisticStatus((prev) => ({ ...prev, [id]: column.status }));
      setSavingIds((prev) => new Set(prev).add(id));
      try {
        await onStatusChange(dragged.transaction, column.status);
      } finally {
        setOptimisticStatus((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setSavingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [drag, onSelectTransaction, onStatusChange, businessInfo?.timezone],
  );

  // Day navigation from the toolbar. Card view is a single day, so PREV/NEXT
  // step one day; TODAY jumps to now. Changing selectedDate is enough — the
  // CalendarContext expands the fetch range and refilters on its own.
  const handleNavigate = useCallback(
    (action: "PREV" | "NEXT" | "TODAY") => {
      if (action === "TODAY") {
        setSelectedDate(new Date());
        return;
      }
      const delta = action === "PREV" ? -1 : 1;
      setSelectedDate(moment(selectedDate).add(delta, "day").toDate());
    },
    [selectedDate, setSelectedDate],
  );

  return (
    <Box className={styles.root}>
      <CalendarToolbar
        date={selectedDate}
        view="card"
        onNavigate={handleNavigate}
      />
      <ScrollArea className={styles.container} type="scroll">
        <Box className={styles.board}>
          {visibleColumns.map((column) => (
            <KanbanColumn
              key={column.id}
              column={column}
              transactions={groupedByStatus[column.id] || []}
              onSelectTransaction={onSelectTransaction}
              timezone={businessInfo?.timezone || "America/New_York"}
              employeesById={employeesById}
              isDragTarget={overColumnId === column.id}
              draggingId={drag?.transaction.transaction.id ?? null}
              savingIds={savingIds}
              canDrag={canDrag}
              onCardDragStart={handleDragStart}
              onCardDragEnd={handleDragEnd}
              onColumnDragOver={setOverColumnId}
              onColumnDragLeave={(id) =>
                setOverColumnId((prev) => (prev === id ? null : prev))
              }
              onColumnDrop={handleDrop}
            />
          ))}
        </Box>
      </ScrollArea>
    </Box>
  );
};

// Kanban Column Component
interface KanbanColumnProps {
  column: ColumnDescriptor;
  transactions: GetTransactionsResponse[];
  onSelectTransaction: (transaction: GetTransactionsResponse) => void;
  timezone: string;
  employeesById: Map<string, Employee>;
  isDragTarget: boolean;
  draggingId: string | null;
  savingIds: Set<string>;
  canDrag: boolean;
  onCardDragStart: (transaction: GetTransactionsResponse) => void;
  onCardDragEnd: () => void;
  onColumnDragOver: (columnId: string) => void;
  onColumnDragLeave: (columnId: string) => void;
  onColumnDrop: (column: ColumnDescriptor) => void;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  transactions,
  onSelectTransaction,
  timezone,
  employeesById,
  isDragTarget,
  draggingId,
  savingIds,
  canDrag,
  onCardDragStart,
  onCardDragEnd,
  onColumnDragOver,
  onColumnDragLeave,
  onColumnDrop,
}) => {
  return (
    <Box
      data-column-id={column.id}
      className={`${styles.column} ${isDragTarget ? styles.columnDragTarget : ""}`}
      style={columnAccentStyle(column)}
      onDragOver={(e) => {
        if (!column.isDroppable) return;
        // preventDefault is what marks this element as a valid drop target.
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onColumnDragOver(column.id);
      }}
      onDragLeave={() => onColumnDragLeave(column.id)}
      onDrop={(e) => {
        if (!column.isDroppable) return;
        e.preventDefault();
        onColumnDrop(column);
      }}
    >
      {/* Column Header */}
      <Group
        justify="space-between"
        gap="xs"
        wrap="nowrap"
        className={styles.columnHeader}
      >
        <Group gap={6} wrap="nowrap" miw={0}>
          <Text
            component="span"
            fz={14}
            fw={500}
            className={styles.columnTitle}
          >
            {column.title}
          </Text>
        </Group>
        <Text component="span" fz={11} fw={700} className={styles.columnCount}>
          {transactions.length}
        </Text>
      </Group>

      {/* Column Content */}
      <Box className={styles.cardList}>
        {transactions.length === 0 ? (
          <Text fz={12} className={styles.emptyState}>
            No appointments
          </Text>
        ) : (
          transactions.map((transaction) => (
            <KanbanCard
              key={transaction.transaction.id}
              transaction={transaction}
              onClick={() => onSelectTransaction(transaction)}
              timezone={timezone}
              employeesById={employeesById}
              // Completed is terminal: those cards can't be dragged back out.
              isDraggable={canDrag && !column.opensTransactionOnDrop}
              isDragging={draggingId === transaction.transaction.id}
              isSaving={savingIds.has(transaction.transaction.id)}
              onDragStart={() => onCardDragStart(transaction)}
              onDragEnd={onCardDragEnd}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

// Kanban Card Component
interface KanbanCardProps {
  transaction: GetTransactionsResponse;
  onClick: () => void;
  timezone: string;
  employeesById: Map<string, Employee>;
  isDraggable: boolean;
  isDragging: boolean;
  isSaving: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
}

const KanbanCard: React.FC<KanbanCardProps> = ({
  transaction,
  onClick,
  timezone,
  employeesById,
  isDraggable,
  isDragging,
  isSaving,
  onDragStart,
  onDragEnd,
}) => {
  const badgeSettings = badgeSettingsFrom(
    (useSalon() as { businessInfo?: unknown }).businessInfo,
  );
  const services = transaction.line_items.booked_services;
  const firstService = services[0];

  // Every provider on the appointment, de-duplicated and in booking order.
  const team = useMemo(() => {
    const seen = new Set<string>();
    return services
      .filter((s) => {
        if (seen.has(s.staff_id)) return false;
        seen.add(s.staff_id);
        return true;
      })
      .map((s) => {
        const employee = employeesById.get(s.staff_id);
        return {
          id: s.staff_id,
          name: s.staff_name,
          firstName: employee?.first_name ?? s.staff_name.split(" ")[0] ?? "",
          lastName:
            employee?.last_name ?? s.staff_name.split(" ").slice(1).join(" "),
          photoUrl: employee?.staff_photo_url ?? employee?.avatar_url ?? null,
        };
      });
  }, [services, employeesById]);

  if (!firstService) return null;

  const clientName = `${transaction.client.first_name} ${transaction.client.last_name}`;
  const serviceName = firstService.name;

  const startTime = moment
    .utc(firstService.start_dt)
    .tz(timezone)
    .format("h:mm A");
  const endTime = moment
    .utc(firstService.calc__end_dt)
    .tz(timezone)
    .format("h:mm A");

  // A single provider gets one large avatar; two or more get a smaller,
  // overlapping stack. Past three, the second slot becomes a "+N" chip.
  const isMulti = team.length > 1;
  const avatarSize = isMulti ? 38 : 52;
  const visible = team.length > 2 ? team.slice(0, 1) : team;
  const overflowCount = team.length - visible.length;

  const flags = transactionFlags(transaction, timezone, badgeSettings);
  const hasFlags = flags.length > 0;
  const serviceColor = firstService.calendar_event_color || "var(--brand-teal)";

  return (
    <Paper
      radius={12}
      style={{ "--svc": serviceColor } as React.CSSProperties}
      className={[
        styles.card,
        blockStyles.tintCard,
        isDraggable ? styles.cardDraggable : "",
        isDragging ? styles.cardDragging : "",
        isSaving ? styles.cardSaving : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-busy={isSaving || undefined}
      draggable={isDraggable && !isSaving}
      onDragStart={(e) => {
        // Firefox only starts a drag when dataTransfer carries something.
        e.dataTransfer.effectAllowed = "move";
        try {
          e.dataTransfer.setData("text/plain", transaction.transaction.id);
        } catch {
          // Older browsers can throw here; the drag still works via state.
        }
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Staff avatar stack */}
      <Box className={styles.avatars}>
        {visible.map((member, index) => (
          // StaffAvatar isn't a forwardRef component, so Tooltip anchors to a
          // Box wrapper (which is) rather than to the avatar itself.
          <Tooltip key={member.id} label={member.name} withArrow>
            <Box
              className={index > 0 ? styles.avatarStacked : undefined}
              style={{ zIndex: visible.length - index }}
            >
              <StaffAvatar
                photoUrl={member.photoUrl}
                firstName={member.firstName}
                lastName={member.lastName}
                size={avatarSize}
                color="burntOrange"
                className={styles.avatar}
                style={{ fontSize: isMulti ? 12 : 16 }}
              />
            </Box>
          </Tooltip>
        ))}
        {overflowCount > 0 && (
          <Tooltip
            label={team
              .slice(visible.length)
              .map((m) => m.name)
              .join(", ")}
            withArrow
          >
            <Box
              className={`${styles.avatar} ${styles.avatarStacked} ${styles.avatarOverflow}`}
              w={avatarSize}
              h={avatarSize}
              style={{
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
              }}
            >
              +{overflowCount}
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Time → client → service, matching the design reference's
          hierarchy, then the badges on their own row */}
      <Box className={styles.cardBody}>
        <Text
          component="span"
          fz={11}
          className={`${styles.cardTime} ${blockStyles.monoTime}`}
        >
          {formatTimeRange(startTime, endTime)}
        </Text>
        <Text component="span" fz={13} fw={500} className={styles.cardClient}>
          {clientName}
        </Text>
        <Text component="span" fz={11} className={styles.cardService}>
          {serviceName}
        </Text>
        {hasFlags && (
          <Box className={styles.cardFlags}>
            <EventFlags flags={flags} inline wrap />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default CardView;
